/**
 * Upload — pure logic.
 *
 * File routing, the phase → job-role defaulting for job-order PDFs, the
 * review derivations (distinct phases, work-centre resolution) and the two
 * payload builders. Nothing here touches React or the server, so every rule
 * about what an upload becomes can be read — and tested — in one place.
 */
import {
	CreateLaborStandardDto,
	CreateOperationDto,
	JobOrderParseResult,
	OperationType,
	ParsedRecipeStandard,
} from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import { isMachinePhase } from "../../utils/standard-matching";

// ---------------------------------------------------------------------------
// File routing
// ---------------------------------------------------------------------------

export type FileKind = "csv" | "excel" | "pdf" | "unsupported";

/** True for the spreadsheet kinds, which share the recipe-parse path. */
export const isSpreadsheet = (kind: FileKind | null): boolean => kind === "csv" || kind === "excel";

export const SUPPORTED_COPY = "Excel, CSV and Holman job-order PDFs are supported";

// Route on the extension first (the reliable signal), MIME second. Anything
// that matches neither is refused - it is never fed to a parser.
export const classifyFile = (candidate: File): FileKind => {
	const name = (candidate.name || "").toLowerCase();
	if (name.endsWith(".csv")) return "csv";
	if (name.endsWith(".xlsx") || name.endsWith(".xls")) return "excel";
	if (name.endsWith(".pdf")) return "pdf";

	const mime = (candidate.type || "").toLowerCase();
	if (mime === "text/csv" || mime === "application/csv") return "csv";
	if (mime === "application/pdf") return "pdf";
	if (mime.includes("spreadsheetml") || mime === "application/vnd.ms-excel") return "excel";
	return "unsupported";
};

// ---------------------------------------------------------------------------
// Master data shapes
// ---------------------------------------------------------------------------

export interface MasterWorkcenter {
	id: number;
	name: string;
}

export interface MasterJob {
	id: number;
	name: string;
}

// ---------------------------------------------------------------------------
// Phase -> job role defaulting
// ---------------------------------------------------------------------------

// Document phases are the customer's vocabulary ("100 Installation",
// "900 Inspection"); job roles are Wilya's. These hints pick a sensible
// starting role per phase - the user can always override in the review step.
const PHASE_ROLE_HINTS: { phase: RegExp; roles: RegExp[] }[] = [
	{
		phase: /inspect|quality|q\.?c\.?|sign[\s-]?off|audit/i,
		roles: [/inspect/i, /technician\s*(iii|3)/i, /quality/i],
	},
	{
		phase: /install|upfit|mount|body|fit[\s-]?up|build/i,
		roles: [/mechanical/i, /install/i, /upfit/i, /assembl/i],
	},
	{
		phase: /service|repair|diagnos|pdi|prep|clean/i,
		roles: [/service/i, /technician\s*(ii|2)\b/i, /mechanic/i],
	},
	{
		phase: /electric|wir|harness|light/i,
		roles: [/electric/i, /wir/i],
	},
	{
		phase: /paint|coat|decal|graphic|finish/i,
		roles: [/paint/i, /coat/i, /finish/i],
	},
	{
		phase: /weld|fabricat|machin/i,
		roles: [/weld/i, /fabricat/i, /machin/i],
	},
];

export const defaultRoleForPhase = (description: string, roles: MasterJob[]): string => {
	if (roles.length === 0) return "";
	const text = description || "";
	const group = PHASE_ROLE_HINTS.find((entry) => entry.phase.test(text));
	if (group) {
		for (const roleHint of group.roles) {
			const hit = roles.find((role) => roleHint.test(role.name));
			if (hit) return hit.name;
		}
	}
	// Last resort before the fallback: a shared word between phase and role.
	const words = text.toLowerCase().match(/[a-z]{4,}/g) || [];
	const wordHit = roles.find((role) => words.some((word) => role.name.toLowerCase().includes(word)));
	return (wordHit || roles[0]).name;
};

export const formatHours = (hours: number): string => `${(hours || 0).toFixed(2)} h`;

// ---------------------------------------------------------------------------
// Payload shapes
// ---------------------------------------------------------------------------

// The create payload plus the two flags the backend accepts alongside it.
export type StandardPayload = CreateLaborStandardDto & {
	isActive?: boolean;
	totalDurationMinutes?: number;
};

/** What saveStandards writes: the payload, and the standard it updates if any. */
export interface StandardWrite {
	payload: StandardPayload;
	existingId?: number | null;
}

// ---------------------------------------------------------------------------
// PDF review
// ---------------------------------------------------------------------------

export interface DistinctPhase {
	phaseCode: string;
	description: string;
	occurrences: number;
	totalHours: number;
	// Set when the server matched this document code to one of the company's
	// configured work phases (Setup -> Work phases). That match is what carries
	// the suggested job role, and what makes this import self-mapping.
	workPhaseId: number | null;
	workPhaseName: string;
	suggestedJobRoleId: number | null;
	suggestedJobRole: string;
}

/** One row per DISTINCT document phase code - that is what gets mapped. */
export const distinctPhasesOf = (pdfResult: JobOrderParseResult | null): DistinctPhase[] => {
	if (!pdfResult) return [];
	const map = new Map<string, DistinctPhase>();
	pdfResult.operations.forEach((op) => {
		op.phases.forEach((phase) => {
			const existing = map.get(phase.phaseCode);
			if (existing) {
				existing.occurrences += 1;
				existing.totalHours += phase.hours || 0;
				if (!existing.description && phase.description) existing.description = phase.description;
				if (existing.workPhaseId === null && phase.matchedWorkPhaseId) {
					existing.workPhaseId = phase.matchedWorkPhaseId;
					existing.workPhaseName = phase.matchedWorkPhaseName || "";
					existing.suggestedJobRoleId = phase.suggestedJobRoleId ?? null;
					existing.suggestedJobRole = phase.suggestedJobRole || "";
				}
			} else {
				map.set(phase.phaseCode, {
					phaseCode: phase.phaseCode,
					description: phase.description || "",
					occurrences: 1,
					totalHours: phase.hours || 0,
					workPhaseId: phase.matchedWorkPhaseId ?? null,
					workPhaseName: phase.matchedWorkPhaseName || "",
					suggestedJobRoleId: phase.suggestedJobRoleId ?? null,
					suggestedJobRole: phase.suggestedJobRole || "",
				});
			}
		});
	});
	return Array.from(map.values()).sort((a, b) => a.phaseCode.localeCompare(b.phaseCode));
};

/**
 * The role a configured work phase brings with it, resolved against the
 * company's real job roles (id first, then name). Empty when the code is not
 * configured or its work phase has no default role.
 */
export const workPhaseRoleOf = (phase: DistinctPhase, jobRoles: MasterJob[]): string => {
	if (phase.suggestedJobRoleId) {
		const byId = jobRoles.find((role) => role.id === phase.suggestedJobRoleId);
		if (byId) return byId.name;
	}
	if (phase.suggestedJobRole) {
		const wanted = phase.suggestedJobRole.trim().toLowerCase();
		const byName = jobRoles.find((role) => role.name.trim().toLowerCase() === wanted);
		if (byName) return byName.name;
	}
	return "";
};

export interface PdfPayloadArgs {
	pdfResult: JobOrderParseResult;
	operationNames: Record<number, string>;
	operationTypes: OperationType[];
	phaseRoles: Record<string, string>;
	products: Product[];
	productId: number | null;
	workcenter: string;
}

/**
 * One labor standard for the chosen product + work centre. Operation hours
 * and phase hours both come from the document; phase start offsets run
 * sequentially inside each operation.
 */
export const buildPdfPayload = ({
	pdfResult,
	operationNames,
	operationTypes,
	phaseRoles,
	products,
	productId,
	workcenter,
}: PdfPayloadArgs): StandardPayload => {
	const descriptionParts = [
		pdfResult.sourceReference ? `Job order ${pdfResult.sourceReference}` : null,
		pdfResult.revision ? `Rev ${pdfResult.revision}` : null,
		pdfResult.customer || null,
		pdfResult.description || null,
	].filter(Boolean);

	const operations = [...pdfResult.operations]
		.sort((a, b) => a.sequence - b.sequence)
		.map((op, index) => {
			const name = (operationNames[op.sequence] || "").trim() || op.name;
			const operationType = operationTypes.find((ot) => ot.name.toLowerCase() === name.toLowerCase());
			let offset = 0;
			const phases = op.phases.map((phase, phaseIndex) => {
				const durationMinutes = Math.round((phase.hours || 0) * 60);
				const built = {
					jobRole: phaseRoles[phase.phaseCode] || "",
					// Keep the link to the company's work phase, so the saved phase can
					// be read in the customer's vocabulary and reported on by work type.
					workPhaseId: phase.matchedWorkPhaseId ?? null,
					workersRequired: 1,
					sequence: phaseIndex + 1,
					startOffsetMinutes: offset,
					durationMinutes,
					notes: phase.description ? `Phase ${phase.phaseCode} - ${phase.description}` : `Phase ${phase.phaseCode}`,
				};
				offset += durationMinutes;
				return built;
			});

			return {
				name,
				operationTypeId: operationType?.id || operationTypes[0]?.id || 1,
				sequence: index + 1,
				// A job order is one bay from start to finish: every step runs on
				// the chosen work centre.
				workstation: workcenter,
				durationMinutes: Math.round((op.totalHours || 0) * 60),
				notes: op.notes,
				phases,
			};
		});

	const boundProduct = products.find((p) => p.id === productId);

	return {
		productId: productId || undefined,
		sku: pdfResult.specCode || boundProduct?.itemNumber || pdfResult.sourceReference,
		workstation: workcenter,
		batchSize: 1,
		description: descriptionParts.join(" · "),
		isActive: true,
		operations,
	};
};

// ---------------------------------------------------------------------------
// Recipe (spreadsheet) review
// ---------------------------------------------------------------------------

/**
 * The company work centre a sheet work centre resolves to: the user's
 * mapping first, then what the server matched. Empty = still unknown.
 */
export const resolveWorkstationWith =
	(workstationMap: Record<string, string>) =>
	(raw: string, resolved?: string | null): string =>
		workstationMap[raw] || resolved || "";

export type ResolveWorkstation = ReturnType<typeof resolveWorkstationWith>;

/**
 * Work centres with phases the sheet left without a job role (SAP recipes
 * carry hours, not crews), keyed by the RESOLVED work centre — one dropdown
 * per work centre, since the same line is staffed the same way on every
 * recipe that runs on it.
 */
export const workstationsNeedingRoleOf = (
	standards: ParsedRecipeStandard[],
	resolveWorkstation: ResolveWorkstation
): string[] => {
	const names = new Set<string>();
	standards.forEach((std) =>
		std.operations.forEach((op) => {
			if (op.phases.some((phase) => phase.needsJobRole && !phase.jobRoleId)) {
				names.add(resolveWorkstation(op.rawWorkstation, op.workstation) || op.rawWorkstation);
			}
		})
	);
	return Array.from(names).sort();
};

export interface RecipeProductGroup {
	productName: string;
	productId?: number | null;
	standards: ParsedRecipeStandard[];
}

/**
 * The standards grouped by product, so a product with a MAIN and an ALT
 * recipe reads as one product with two standards.
 */
export const groupRecipesByProduct = (standards: ParsedRecipeStandard[]): RecipeProductGroup[] => {
	const groups = new Map<string, RecipeProductGroup>();
	standards.forEach((std) => {
		const key = std.productId != null ? `id:${std.productId}` : `key:${std.productKey}`;
		const group = groups.get(key) || { productName: std.productName || std.productKey, productId: std.productId, standards: [] };
		group.standards.push(std);
		groups.set(key, group);
	});
	return Array.from(groups.values());
};

export interface RecipeWritesArgs {
	standards: ParsedRecipeStandard[];
	resolveWorkstation: ResolveWorkstation;
	operationTypes: OperationType[];
	/** Resolved work centre -> job role id, for phases the sheet left without one. */
	workstationRoles: Record<string, number | "">;
	jobRoles: MasterJob[];
	fileName?: string;
}

/**
 * One write per parsed standard: operations carry their own work centre (and
 * alternatives), phases carry the sheet's role or the one picked for the
 * work centre. The server recomputes durations.
 */
export const buildRecipeWrites = ({
	standards,
	resolveWorkstation,
	operationTypes,
	workstationRoles,
	jobRoles,
	fileName,
}: RecipeWritesArgs): StandardWrite[] =>
	standards.map((std) => {
		const operations: CreateOperationDto[] = [...std.operations]
			.sort((a, b) => a.sequence - b.sequence)
			.map((op, opIndex) => {
				const workstation = resolveWorkstation(op.rawWorkstation, op.workstation);
				const operationType =
					operationTypes.find((ot) => ot.id === op.operationTypeId) ||
					operationTypes.find((ot) => ot.name.toLowerCase() === op.name.toLowerCase());
				const roleId = workstationRoles[workstation || op.rawWorkstation];
				const role = roleId ? jobRoles.find((jr) => jr.id === roleId) : undefined;
				return {
					name: op.name,
					operationTypeId: operationType?.id || op.operationTypeId || operationTypes[0]?.id || 1,
					sequence: opIndex + 1,
					workstation,
					workstationOptions: op.workstationOptions
						.map((option) => ({
							workstation: resolveWorkstation(option.rawWorkstation, option.workstation),
							durationMinutes: option.durationMinutes ?? null,
							isDefault: false,
							notes: option.fromRecipeGroup ? `From recipe group ${option.fromRecipeGroup}` : undefined,
						}))
						.filter((option) => option.workstation && option.workstation !== workstation),
					durationMinutes: op.durationMinutes,
					notes: op.code ? `Operation ${op.code}` : undefined,
					phases: op.phases.map((phase, phaseIndex) => {
						// A machine line (0 workers) occupies the unit and needs nobody:
						// no job role is picked for it. Crew sizes go through as the
						// sheet states them, decimals included.
						const machine = isMachinePhase(phase);
						return {
							jobRoleId: machine ? phase.jobRoleId ?? null : phase.jobRoleId ?? role?.id ?? null,
							jobRole: machine ? phase.jobRole || "" : phase.jobRole || role?.name || "",
							workPhaseId: phase.workPhaseId ?? null,
							workersRequired: phase.workersRequired ?? 1,
							sequence: phaseIndex + 1,
							startOffsetMinutes: phase.startOffsetMinutes || 0,
							durationMinutes: phase.durationMinutes,
							notes:
								phase.laborHours != null || phase.machineHours != null
									? [
										phase.laborHours != null ? `${phase.laborHours} labor h` : null,
										phase.machineHours != null ? `${phase.machineHours} machine h` : null,
									]
										.filter(Boolean)
										.join(" · ")
									: undefined,
						};
					}),
				};
			});

		return {
			existingId: std.existingStandardId ?? null,
			payload: {
				productId: std.productId ?? undefined,
				sku: std.productKey,
				workstation: operations[0]?.workstation || null,
				recipeGroup: std.recipeGroup ?? null,
				batchSize: std.batchSize,
				batchUnit: std.batchUnit ?? null,
				totalDurationMinutes: 0, // Will be calculated by backend
				description: std.description || `Uploaded from ${fileName}`,
				isActive: true,
				operations,
			},
		};
	});
